import React, { useState } from "react";

// Added onShowDetailsModal prop for the button click
const JobCard = ({ job, onSelectCard, onShowDetailsModal }) => {
  // Destructure ALL expected fields from the transformed job data
  const {
    id,
    companyName = "N/A",
    jobTitle = "N/A",
    jobType = "N/A",
    salary = "N/A",
    level = "N/A",
    location = "N/A",
    remote = "N/A",
    applicants = "N/A", // This comes from transformJobData
    matchPercentage = 0,
    link = "#", // Ensure link is destructured
    postedDate = null, // Add postedDate field
    thumbnail = null, // Add thumbnail field
  } = job || {}; // Add default empty object to prevent errors if job is null/undefined briefly

  // State for handling image loading errors
  const [imageError, setImageError] = useState(false);

  // Round the matchPercentage to the nearest whole number
  const roundedMatchPercentage = Math.round(matchPercentage);

  // Click handler for the entire card div (selects for optimization)
  const handleCardClick = () => {
    if (onSelectCard) {
      onSelectCard(job);
    }
  };

  // Click handler specifically for the "View Job Details" button
  const handleViewDetailsClick = (e) => {
    e.stopPropagation(); // Prevent card click
    if (onShowDetailsModal) {
      onShowDetailsModal(job); // Show modal
    }
  };

  // Handle image load error
  const handleImageError = () => {
    setImageError(true);
  };

  // --- Logo Generation Logic ---
  const getLogoBackground = () => {
    if (!companyName || companyName === "N/A") return "#cccccc";
    const colors = [
      "#4285F4",
      "#EA4335",
      "#FBBC05",
      "#34A853",
      "#FF6D01",
      "#46BDC6",
      "#7E57C2",
      "#FF8A65",
      "#1DA1F2",
      "#0077B5",
    ];
    let hash = 0;
    if (companyName.length === 0) return colors[0];
    for (let i = 0; i < companyName.length; i++) {
      hash = companyName.charCodeAt(i) + ((hash << 5) - hash);
      hash = hash & hash;
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };
  const getLogoInitials = () => {
    if (!companyName || companyName === "N/A") return "?";
    const words = companyName.split(" ").filter(Boolean);
    if (words.length === 0) return "?";
    return words.length > 1
      ? (words[0][0] + words[words.length - 1][0]).toUpperCase()
      : words[0].substring(0, 2).toUpperCase();
  };
  // --- End Logo Generation ---

  // --- Match/Strength Helpers ---
  const getMatchColor = (percentage) => {
    if (percentage >= 0) return "#4CAF50";
    return "#e0e0e0";
  };
  const getStrengthBarColor = (index, percentage) => {
    const barCount = 10;
    const filledBars = Math.round((percentage / 100) * barCount);
    if (index >= filledBars) return "#e9edf2";
    const colors = [
      "#FF3D00",
      "#FF5722",
      "#FF7043",
      "#FF9800",
      "#FFA726",
      "#FFC107",
      "#FFCA28",
      "#CDDC39",
      "#8BC34A",
      "#4CAF50",
    ];
    const colorIndex = Math.min(index, colors.length - 1);
    return colors[colorIndex];
  };
  const getMatchStrengthText = (percentage) => {
    if (percentage >= 70) return "Strong Match";
    if (percentage >= 40) return "Good Match";
    return "Fair Match";
  };
  const tagStyle = {
    padding: "4px 10px",
    borderRadius: "16px",
    backgroundColor: "#f3f4f6",
    fontSize: "12px",
    fontWeight: "500",
    color: "#4b5563",
    whiteSpace: "nowrap",
    display: "inline-block",
    lineHeight: "1.4",
  };
  // --- End Helpers ---

  // Add a check in case job data is temporarily missing during render
  if (!job) {
    return null; // Or a loading state for the card
  }

  // Determine whether to show image or initials
  const showImage = thumbnail && !imageError;

  return (
    <div
      className="job-card"
      onClick={handleCardClick} // Selects for optimization
      // Inline styles kept from previous version for consistency
      style={{
        display: "flex",
        flexDirection: "column",
        padding: "20px",
        borderRadius: "16px",
        backgroundColor: "#fff",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
        cursor: "pointer",
        transition: "box-shadow 0.2s ease-in-out",
        fontFamily: "'Inter', sans-serif",
        position: "relative",
        gap: "16px",
      }}
    >
      {/* Header Section */}
      <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
        <div
          style={{
            width: "56px",
            height: "56px",
            borderRadius: "12px",
            backgroundColor: showImage ? "#f9fafb" : getLogoBackground(),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            flexShrink: 0,
            color: "#ffffff",
            fontSize: "20px",
            fontWeight: "600",
          }}
          aria-label={`${companyName} logo`}
        >
          {showImage ? (
            <img
              src={thumbnail}
              alt={`${companyName} logo`}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                padding: "8px",
              }}
              onError={handleImageError}
            />
          ) : (
            <span className="initials-fallback">{getLogoInitials()}</span>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3
            style={{
              margin: "0 0 4px 0",
              fontSize: "20px",
              fontWeight: "600",
              color: "#1f2937",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {" "}
            {jobTitle}{" "}
          </h3>
          <p style={{ margin: 0, fontSize: "14px", color: "#6b7280" }}>
            {" "}
            {companyName}{" "}
          </p>
        </div>
        <button
          style={{
            background: "#f3f4f6",
            border: "none",
            color: "#9ca3af",
            cursor: "pointer",
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            transition: "background-color 0.2s",
          }}
          onClick={(e) => {
            e.stopPropagation();
            console.log("Bookmark clicked:", id);
          }}
          aria-label="Bookmark this job"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {" "}
            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>{" "}
          </svg>
        </button>
      </div>

      {/* Main Content Section */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: "16px",
        }}
      >
        {/* Left Column */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            flex: 1,
            minWidth: 0,
          }}
        >
          {/* Tags */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              lineHeight: "1.5",
            }}
          >
            {postedDate && <span style={tagStyle}>📅 {postedDate}</span>}
            {jobType !== "N/A" && <span style={tagStyle}>{jobType}</span>}
            {salary !== "N/A" && salary !== "Competitive" && (
              <span style={tagStyle}>{salary}</span>
            )}
            {level !== "N/A" && <span style={tagStyle}>{level}</span>}
            {location !== "N/A" && <span style={tagStyle}>{location}</span>}
            {remote === "Remote" && <span style={tagStyle}>Remote</span>}
          </div>
          {/* Applicants */}
          {applicants !== "N/A" && applicants !== "Apply Now" && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginTop: "4px",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 20 20"
                fill="currentColor"
                style={{ color: "#9ca3af" }}
                aria-hidden="true"
              >
                {" "}
                <path
                  fillRule="evenodd"
                  d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm2 1a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zM6 9a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                  clipRule="evenodd"
                />{" "}
              </svg>
              <span
                style={{
                  fontSize: "13px",
                  color: "#6b7280",
                  fontWeight: "500",
                }}
              >
                {" "}
                {applicants}{" "}
              </span>
            </div>
          )}
          {/* View Details Button */}
          <button
            style={{
              padding: "10px 16px",
              backgroundColor: "#065f46",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              marginTop: "auto",
              transition: "background-color 0.2s",
              alignSelf: "flex-start",
            }}
            onClick={handleViewDetailsClick} // Use specific handler
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#047857")
            } // Keep hover effects
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "#065f46")
            }
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              {" "}
              <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />{" "}
              <path
                fillRule="evenodd"
                d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.08 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                clipRule="evenodd"
              />{" "}
            </svg>
            <span> View Job Details </span>
          </button>
        </div>

        {/* Right Column */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
            flexShrink: 0,
          }}
        >
          <div style={{ position: "relative", width: "80px", height: "80px" }}>
            <svg width="80" height="80" viewBox="0 0 80 80">
              <circle
                cx="40"
                cy="40"
                r="32"
                fill="none"
                stroke="#e9edf2"
                strokeWidth="6"
              />
              <circle
                cx="40"
                cy="40"
                r="32"
                fill="none"
                stroke={getMatchColor(roundedMatchPercentage)}
                strokeWidth="6"
                strokeDasharray={`${2 * Math.PI * 32}`}
                strokeDashoffset={`${
                  2 * Math.PI * 32 * (1 - roundedMatchPercentage / 100)
                }`}
                transform="rotate(-90 40 40)"
                strokeLinecap="round"
                style={{ transition: "stroke-dashoffset 0.5s ease-out" }}
              />
              <text
                x="40"
                y="40"
                textAnchor="middle"
                dominantBaseline="central"
                fill="#1f2937"
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "12px",
                  fontWeight: "bold",
                }}
              >
                <tspan
                  x="40"
                  dy="-3"
                  dx="1"
                  style={{
                    fontWeight: "bold",
                    fontSize: "14px",
                    fill: getMatchColor(roundedMatchPercentage),
                  }}
                >
                  {roundedMatchPercentage}%
                </tspan>
                <tspan
                  x="40"
                  dy="12"
                  style={{
                    fontWeight: "600",
                    fill: "#6b7280",
                    fontSize: "7.5px",
                  }}
                >
                  {getMatchStrengthText(roundedMatchPercentage)}
                </tspan>
              </text>
            </svg>
          </div>
          {/* Strength Bars */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "3px",
                marginBottom: "6px",
                alignItems: "flex-end",
                height: "20px",
              }}
            >
              {Array.from({ length: 10 }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    width: "5px",
                    height: `${6 + i * 1.4}px`,
                    backgroundColor: getStrengthBarColor(
                      i,
                      roundedMatchPercentage
                    ),
                    borderRadius: "2px",
                    transition: "background-color 0.3s ease",
                  }}
                ></div>
              ))}
            </div>
            <span
              style={{ fontSize: "12px", color: "#9ca3af", fontWeight: "500" }}
            >
              {" "}
              Profile Strength{" "}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobCard;
